import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTemplate } from "@hooks/useAppTemplate";
import useMixedItemDist from "@hooks/useMixedItemDist";
import {
	getAnsweredCount,
	getFallbackProficiencyLevelLabel,
	groupQuestionTypesBySkill,
} from "@memorang/helpers/language-learning";
import {
	QUESTION_TYPE_MASTERY_WINDOW,
	resolveMasteryForTagId,
} from "@memorang/helpers/language-learning-mastery";
import {
	compareTagSortOrder,
	isProficiencyTag,
	isQuestionTypeTag,
	isSkillTag,
} from "@memorang/helpers/tag";
import type { ItemTypesSectionItem } from "@memorang/ui/components/HomeApplet/types";
import {
	buildProficiencyIconMap,
	getQuestionTypeIcon,
	getSkillIcon,
} from "@memorang/ui/constants";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	LANGUAGE_LEARNING_HOME_SKILLS_USE_CASE,
	LANGUAGE_LEARNING_TIP_LEVEL_GUIDANCE,
	buildLanguageLearningTipDataKey,
	buildSkillsTipData,
	buildTipSurfaceState,
	createHomeSkillsTipSchema,
	getCanonicalSkillKey,
	withTipAppFraming,
} from "../../tips/language-learning";
import { tipQueryOptions } from "../../tips/tipQueryOptions";
import { useParsedRecommendationContext } from "./useParsedRecommendationContext";

const EMPTY_MASTERY: ReturnType<typeof resolveMasteryForTagId> = {
	fills: [],
	estimatedLevel: undefined,
	displayLevel: undefined,
};

export const useItemTypesSection = () => {
	const { data: commonDistribution, isLoading } = useMixedItemDist();
	const recommendationContext = useParsedRecommendationContext();
	const proficiencyLevelsConfig = useAppStore(
		(state) => state.app.proficiencyLevels,
	);
	const hideTotalItemCounts = useAppStore(
		(state) => state.app.hideTotalItemCounts,
	);
	const { userPublicMetadataId } = useAuthContext();
	const {
		currentExamContext: { examId },
	} = useExamContext();
	const { template, isLanguageLearningApp } = useAppTemplate();
	const appId = useAppStore((state) => state.app.id);
	const { t, i18n } = useTranslation();

	const proficiencyIconMap = buildProficiencyIconMap(proficiencyLevelsConfig);

	const { skillTags, proficiencyTags, questionTypeTags, rowsBySkill } =
		useMemo(() => {
			const tags = commonDistribution?.tags ?? [];
			const skillTags = tags.filter(isSkillTag).sort(compareTagSortOrder);
			const questionTypeTags = tags.filter(isQuestionTypeTag);
			return {
				skillTags,
				proficiencyTags: tags.filter(isProficiencyTag),
				questionTypeTags,
				rowsBySkill: groupQuestionTypesBySkill(skillTags, questionTypeTags),
			};
		}, [commonDistribution]);

	const { tipsEnabled, tipData, tipDataKey, homeSkillsTipSchema } =
		useMemo(() => {
			const surface = buildTipSurfaceState({
				isLanguageLearningApp,
				mixedItemDist: commonDistribution,
				skillTags,
				recommendationContext,
				appId,
				template,
				examId,
				language: i18n.language,
			});
			if (!surface.enabled) {
				return {
					tipsEnabled: false,
					tipData: undefined,
					tipDataKey: undefined,
					homeSkillsTipSchema: createHomeSkillsTipSchema([]),
				};
			}
			return {
				tipsEnabled: true,
				tipData: withTipAppFraming(
					appId,
					template,
					buildSkillsTipData(surface.dist, surface.proficiencySummaries),
				),
				tipDataKey: buildLanguageLearningTipDataKey(surface.cacheContext),
				homeSkillsTipSchema: createHomeSkillsTipSchema(
					surface.recognizedSkillKeys,
				),
			};
		}, [
			isLanguageLearningApp,
			commonDistribution,
			skillTags,
			recommendationContext,
			appId,
			template,
			examId,
			i18n.language,
		]);

	const tipQuery = useQuery(
		tipQueryOptions({
			useCase: LANGUAGE_LEARNING_HOME_SKILLS_USE_CASE,
			data: tipData,
			dataKey: tipDataKey,
			schema: homeSkillsTipSchema,
			userId: userPublicMetadataId,
			outputLanguage: i18n.language,
			additionalInstructions: LANGUAGE_LEARNING_TIP_LEVEL_GUIDANCE,
			enabled: tipsEnabled,
		}),
	);

	const fallbackLevelLabel = getFallbackProficiencyLevelLabel(proficiencyTags);

	// Resolved once per question type instead of once per render inside the
	// row-building loop below — the mastery lookup rescans the recommendation
	// context's cells per progression level, which isn't cheap to repeat.
	const questionTypeMastery = useMemo(
		() =>
			new Map(
				questionTypeTags.map(
					(questionType) =>
						[
							questionType.id,
							resolveMasteryForTagId(
								recommendationContext,
								questionType.id,
								QUESTION_TYPE_MASTERY_WINDOW,
								fallbackLevelLabel,
							),
						] as const,
				),
			),
		[questionTypeTags, recommendationContext, fallbackLevelLabel],
	);

	const items: ItemTypesSectionItem[] = skillTags.map((skill) => {
		const canonicalKey = getCanonicalSkillKey(skill);
		const attempted = getAnsweredCount(skill);
		const total = skill.numItems ?? (skill.items ?? []).length;
		const subtitle = hideTotalItemCounts
			? t("dashboard.itemTypes.cardSubtitleResponses", {
					count: skill.totalResponses,
				})
			: total > 0 && attempted === total
				? t("dashboard.itemTypes.cardSubtitleComplete")
				: t("dashboard.itemTypes.cardSubtitlePartial", { attempted, total });
		return {
			id: String(skill.id),
			title: t("progress.languageLearning.itemTypesCardTitle", {
				skill: skill.title,
			}),
			subtitle,
			icon: getSkillIcon(skill.title),
			tip:
				tipsEnabled && canonicalKey
					? tipQuery.data?.skills[canonicalKey]
					: undefined,
			tipLoading:
				tipsEnabled && canonicalKey !== undefined && tipQuery.isLoading,
			rows: (rowsBySkill.get(skill.id) ?? []).map((questionType) => {
				const { displayLevel: level } =
					questionTypeMastery.get(questionType.id) ?? EMPTY_MASTERY;
				return {
					id: String(questionType.id),
					icon: getQuestionTypeIcon(questionType.title),
					title: questionType.title,
					level,
					levelIcon: level ? proficiencyIconMap[level] : undefined,
				};
			}),
		};
	});

	return {
		isEnabled: items.length > 0,
		isLoading,
		sectionTitle: t("dashboard.skills.title"),
		data: items,
	};
};
